import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import { toast } from 'react-toastify'
import { useAuth } from '../Auth/AuthContext'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  maxWidth: '95%',
  maxHeight: '80%',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  textAlign: 'center',
  overflow: 'auto',
}

export default function AdminRotaModal({
  open,
  onClose,
  dateStr,
  existingEntry,
  onSaved,
}) {
  const { currentUser } = useAuth()
  const [eligibleAdmins, setEligibleAdmins] = useState([])
  const [adminUid, setAdminUid] = useState('')
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [saving, setSaving] = useState(false)

  const isEdit = Boolean(existingEntry?._id)

  useEffect(() => {
    if (!open || !currentUser?.accessToken) return

    setLoadingUsers(true)
    axios
      .get(`${process.env.REACT_APP_API}/users/list-all-users`, {
        headers: { AuthToken: currentUser.accessToken },
      })
      .then((res) => {
        const eligible = (res.data.users || []).filter(
          (user) => user.customClaims?.rotaEligible === true
        )

        // Keep the currently assigned admin visible even if eligibility was removed
        if (
          existingEntry?.adminUid &&
          !eligible.some((user) => user.uid === existingEntry.adminUid)
        ) {
          eligible.push({
            uid: existingEntry.adminUid,
            displayName: existingEntry.adminName,
            email: existingEntry.adminEmail,
            phoneNumber: existingEntry.adminPhone,
          })
        }

        setEligibleAdmins(eligible)
      })
      .catch((error) => {
        console.error(error)
        toast.error('Failed to load rota-eligible admins')
      })
      .finally(() => setLoadingUsers(false))
  }, [open, currentUser, existingEntry])

  useEffect(() => {
    if (!open) return
    setAdminUid(existingEntry?.adminUid || '')
  }, [open, existingEntry])

  const selectedAdmin = useMemo(
    () => eligibleAdmins.find((user) => user.uid === adminUid) || null,
    [eligibleAdmins, adminUid]
  )

  const handleClose = () => {
    setAdminUid('')
    onClose()
  }

  const handleSave = async () => {
    if (!adminUid) {
      toast.error('Please select an admin')
      return
    }
    if (!dateStr) {
      toast.error('Missing date')
      return
    }

    setSaving(true)
    try {
      if (isEdit) {
        await axios.post(
          `${process.env.REACT_APP_API}/admin-rota/update`,
          { _id: existingEntry._id, adminUid, dateStr },
          { headers: { AuthToken: currentUser.accessToken } }
        )
        toast.success('Rota entry updated')
      } else {
        await axios.post(
          `${process.env.REACT_APP_API}/admin-rota/create`,
          { adminUid, dateStr },
          { headers: { AuthToken: currentUser.accessToken } }
        )
        toast.success('Rota entry created')
      }
      handleClose()
      onSaved?.()
    } catch (error) {
      console.error(error)
      const message = error.response?.data?.error || 'Failed to save rota entry'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!existingEntry?._id) return
    if (!window.confirm('Are you sure you want to delete this rota entry?')) return

    setSaving(true)
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/admin-rota/delete`,
        { _id: existingEntry._id },
        { headers: { AuthToken: currentUser.accessToken } }
      )
      toast.success('Rota entry deleted')
      handleClose()
      onSaved?.()
    } catch (error) {
      console.error(error)
      const message = error.response?.data?.error || 'Failed to delete rota entry'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="admin-rota-modal-title"
      aria-describedby="admin-rota-modal-description"
    >
      <Box sx={style}>
        <Typography id="admin-rota-modal-title" variant="h6" component="h2" gutterBottom>
          {isEdit ? 'Edit Admin Rota' : 'Add Admin Rota'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {dateStr} · Full day (UK time)
        </Typography>

        <FormControl fullWidth sx={{ my: 1 }}>
          <InputLabel id="rota-admin-select-label">Admin</InputLabel>
          <Select
            labelId="rota-admin-select-label"
            id="rota-admin-select"
            value={adminUid}
            label="Admin"
            onChange={(e) => setAdminUid(e.target.value)}
            disabled={loadingUsers || saving}
          >
            <MenuItem value="">Select admin</MenuItem>
            {eligibleAdmins.map((user) => (
              <MenuItem key={user.uid} value={user.uid}>
                {user.displayName || user.email || user.uid}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!loadingUsers && eligibleAdmins.length === 0 && (
          <Typography variant="body2" color="error" sx={{ my: 1 }}>
            No rota-eligible admins. Enable Rota on the /admin page first.
          </Typography>
        )}

        <TextField
          variant="outlined"
          fullWidth
          sx={{ my: 1 }}
          label="Name"
          value={selectedAdmin?.displayName || existingEntry?.adminName || ''}
          InputProps={{ readOnly: true }}
        />
        <TextField
          variant="outlined"
          fullWidth
          sx={{ my: 1 }}
          label="Email"
          value={selectedAdmin?.email || existingEntry?.adminEmail || ''}
          InputProps={{ readOnly: true }}
        />
        <TextField
          variant="outlined"
          fullWidth
          sx={{ my: 1 }}
          label="Phone"
          value={selectedAdmin?.phoneNumber || existingEntry?.adminPhone || ''}
          InputProps={{ readOnly: true }}
        />

        <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#395076' }}
            onClick={handleSave}
            disabled={saving || loadingUsers}
          >
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
          {isEdit && (
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </Button>
          )}
          <Button variant="outlined" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
